import {
  Modal,
  ModalVariant,
  Form,
  ActionGroup,
  FormAlert,
  Alert,
  FormGroup,
} from '@patternfly/react-core'
import * as React from 'react'
import { useCallback, useMemo, useState } from 'react'

import { FormInputGroup } from '@app/domain/form/components/form-inputs.component'
import {
  getOrderedQuestions,
  getDisabledQuestions,
  parseFormattedUnitResponseString,
} from '@app/domain/form/form.helpers'
import type { Question } from '@app/domain/instrument'
import type {
  IEntry} from '@app/domain/instrument/instrument.interface';
import {
  QuestionTypeId,
} from '@app/domain/instrument/instrument.interface'
import type { IDataInstrument } from '@app/domain/visit/visit.interface'
import { hasContent } from '@app/lib/string'
import { SmallButton } from '@app/utils/styles/small-button'

interface Props {
  entry: IEntry
  isModalOpen: boolean
  instrument: IDataInstrument
  onCancel: () => void
  onSubmit: (entry: IEntry, responses: Record<string, string>) => void
}

export const EditFormModal = function ({
  entry,
  isModalOpen,
  instrument,
  onCancel,
  onSubmit,
}: Props) {
  const [displayErrorMessage, setDisplayErrorMessage] = useState<boolean>(false)
  const [errorMessage, setErrorMessage] = useState<string>('')
  const [responses, setResponses] = useState<Record<string, string>>(
    entry ? entry.responses : {},
  )

  const disabledQuestions = useMemo(
    () => getDisabledQuestions(instrument, entry, responses),
    [instrument, entry, responses],
  )

  const orderedQuestions = useMemo(() => getOrderedQuestions(instrument), [
    instrument,
  ])

  const isFormValid = useMemo(() => {
    return orderedQuestions.every((question: Question) => {
      if (question.inputType === QuestionTypeId.NumericWithUnits) {
        const parsedNumericUnitsResponse = parseFormattedUnitResponseString(
          responses[question.id],
        )

        if (
          !disabledQuestions.some((v: Question) => v.id === question.id) &&
          question.required &&
          (!hasContent(parsedNumericUnitsResponse?.value) ||
            !hasContent(parsedNumericUnitsResponse?.unit))
        ) {
          return false
        }
      }

      if (
        !disabledQuestions.some((v: Question) => v.id === question.id) &&
        question.required &&
        !responses[question.id]
      ) {
        return false
      } else {
        setErrorMessage('')
        setDisplayErrorMessage(false)
        return true
      }
    })
  }, [disabledQuestions, orderedQuestions, responses])

  const handleOnSubmit = useCallback(() => {
    if (isFormValid) {
      onSubmit(entry, responses)
    } else {
      setErrorMessage('Please Answer All Required Questions')
      setDisplayErrorMessage(true)
    }
  }, [entry, isFormValid, onSubmit, responses])

  const handleCancel = useCallback(() => {
    setResponses(entry.responses)
    onCancel()
  }, [entry.responses, onCancel])

  const onOptionChanged = useCallback(
    (question: string, value: string) => {
      const updatedResponses = {
        ...responses,
        [question]: value,
      }
      setResponses(updatedResponses)
    },
    [responses],
  )

  const handleCheckboxResponseChange = useCallback(
    (question: string, responseValues: string[], value: string) => {
      if (responseValues.findIndex((r) => r === value) >= 0) {
        responseValues = responseValues.filter((r) => r !== value)
      } else {
        responseValues.push(value)
      }
      const updatedResponses = {
        ...responses,
        [question]: responseValues.join('|'),
      }
      setResponses(updatedResponses)
    },
    [responses],
  )

  return (
    <Modal
      variant={ModalVariant.small}
      title="Edit Instrument Responses"
      isOpen={isModalOpen}
      onClose={handleCancel}
    >
      {displayErrorMessage ? (
        <FormAlert>
          <Alert
            variant="danger"
            title={errorMessage}
            aria-live="polite"
            isInline
          />
          <br />
        </FormAlert>
      ) : null}

      <Form isHorizontal>
        {orderedQuestions.map((question: Question, idx) => (
          <FormGroup
            key={question.id}
            isRequired={question.required}
            label={
              <span dangerouslySetInnerHTML={{ __html: question.label }} />
            }
            fieldId={question.id}
            hasNoPaddingTop
          >
            <FormInputGroup
              key={idx}
              question={question}
              isDisabled={disabledQuestions.some(
                (v: Question) => v.id === question.id,
              )}
              handleCheckboxResponseChange={handleCheckboxResponseChange}
              responses={responses}
              onOptionChanged={onOptionChanged}
              showDisableReason
              disabledReason="This question is disabled based on responses to another question."
            />
          </FormGroup>
        ))}
      </Form>
      <ActionGroup style={{ float: 'right', marginTop: 10 }}>
        <SmallButton
          style={{ marginRight: 5 }}
          key="cancel"
          variant="secondary"
          onClick={handleCancel}
        >
          Cancel
        </SmallButton>
        <SmallButton key="confirm" variant="primary" onClick={handleOnSubmit}>
          Save
        </SmallButton>
      </ActionGroup>
    </Modal>
  )
}
